import axios from 'axios';
import { apiClient } from './api';
import type {
  ClockInRequest,
  ClockOutRequest,
  ClockInResponse,
  AttendanceRecord,
  TodayAttendance,
  LocationVerdict,
  CorrectionRequest,
} from '../types/attendance.types';


interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface HistoryResponse {
  records: AttendanceRecord[];
  total: number;
  page: number;
  limit: number;
  /** The office's timezone (e.g. Europe/London); null when the user has no office. */
  timezone?: string | null;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message as string | undefined;
    return msg ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function clockIn(data: ClockInRequest): Promise<ClockInResponse> {
  try {
    const response = await apiClient.post<ApiResponse<ClockInResponse>>(
      '/api/attendance/clock-in',
      data
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to clock in. Please try again.'));
  }
}

/**
 * Close the open session, sending wherever the device says we are.
 *
 * `data` is optional throughout: if geolocation was denied or timed out, clock out
 * anyway. The server records the absent position rather than refusing, because a
 * session nobody can close is worse than one flagged as ended off-site.
 */
export async function clockOut(data: ClockOutRequest = {}): Promise<ClockInResponse> {
  try {
    const response = await apiClient.post<ApiResponse<ClockInResponse>>(
      '/api/attendance/clock-out',
      data
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to clock out. Please try again.'));
  }
}

/** Today's sessions plus the running totals. */
export async function getTodayAttendance(): Promise<TodayAttendance> {
  try {
    const response = await apiClient.get<ApiResponse<TodayAttendance>>(
      '/api/attendance/today'
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to fetch today\'s attendance record.'));
  }
}

/**
 * The open session, else the day's most recent one.
 *
 * Kept for callers that only need "is this person clocked in" — the app shell's
 * status dot — so they did not have to learn about sessions.
 */
export async function getTodayRecord(): Promise<AttendanceRecord | null> {
  const today = await getTodayAttendance();
  return today.record;
}

export async function getHistory(
  page = 1,
  limit = 20
): Promise<HistoryResponse> {
  try {
    const response = await apiClient.get<ApiResponse<HistoryResponse>>(
      `/api/attendance/history?page=${page}&limit=${limit}`
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to fetch attendance history.'));
  }
}

export async function enrolFace(imageBase64: string, gdprConsent: boolean): Promise<void> {
  try {
    await apiClient.post('/api/attendance/enrol-face', { imageBase64, gdprConsent });
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Face enrolment failed. Please try again.'));
  }
}

/**
 * Pre-flight for the clock-in button — the same verdict clock-in itself will reach.
 *
 * Call it with no coordinates first. If the office network already satisfies the
 * configured mode the answer comes back `ok`, and the page never has to ask the
 * browser for a location at all. Only when that fails is GPS worth prompting for.
 */
/** What the clock-in screen may offer. Falls back to everything allowed if the call fails. */
export interface ClockInOptions { manualClockIn: boolean; travelNeedsApproval: boolean }

export async function getClockInOptions(): Promise<ClockInOptions> {
  try {
    const response = await apiClient.get<ApiResponse<ClockInOptions>>(
      '/api/attendance/clock-in-options'
    );
    const data = response.data.data;
    return { manualClockIn: data.manualClockIn ?? true, travelNeedsApproval: data.travelNeedsApproval ?? false };
  } catch {
    return { manualClockIn: true, travelNeedsApproval: false };
  }
}

export async function checkGeofenceClient(
  latitude: number | null = null,
  longitude: number | null = null,
  accuracy: number | null = null
): Promise<LocationVerdict> {
  try {
    const response = await apiClient.post<ApiResponse<LocationVerdict>>(
      '/api/attendance/geofence-check',
      { latitude, longitude, accuracy }
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Location verification failed. Please try again.'));
  }
}

export interface CreateCorrectionInput {
  attendanceRecordId: string;
  field: 'clock_in_at' | 'clock_out_at';
  requestedValue: string;
  reason?: string | null;
}

/**
 * File a self-service correction for a recorded clock-in/out time. Replaces the
 * old "Tell HR if the time is wrong" dead-end — the employee supplies the value
 * they believe is correct and a reason, and HR is notified for review.
 */
export async function createCorrection(
  input: CreateCorrectionInput
): Promise<CorrectionRequest> {
  try {
    const response = await apiClient.post<ApiResponse<CorrectionRequest>>(
      '/api/attendance/corrections',
      input
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Could not submit the correction request.'));
  }
}

/** The employee's own correction requests, newest first. */
export async function getMyCorrections(): Promise<CorrectionRequest[]> {
  try {
    const response = await apiClient.get<ApiResponse<CorrectionRequest[]>>(
      '/api/attendance/corrections/mine'
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Could not load correction requests.'));
  }
}

/**
 * Server-side undo of the most recent punch (the 8-minute "undo" safety window).
 * Reverts the actual attendance record — soft-void for clock-in, reopen for
 * clock-out — so HR no longer sees a phantom entry.
 */
export async function cancelAttendanceRecord(recordId: string): Promise<void> {
  try {
    await apiClient.post<ApiResponse<void>>(`/api/attendance/${recordId}/void`);
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Could not undo the punch.'));
  }
}
